import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { Link, useNavigate, useParams } from 'react-router';

import { CustomUserDTO } from '@/types/api-types';

import IconChevronLeft from '@/assets/svg/icon-chevron-left.svg?react';
import IconCopy from '@/assets/svg/icon-copy.svg?react';
import IconUserAdmin from '@/assets/svg/icon-user-admin.svg?react';
import IconUser from '@/assets/svg/icon-user.svg?react';

import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmationDialog } from '@/components/ConfirmationDialog/ConfirmationDialog';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useAuthStore } from '@/stores/authStore';
import { useGlobalStore } from '@/stores/globalStore';

import { apiService } from '@/services/api-service';

import { INSURANCE_AREA, USER_ROLES } from '@/constants/global-constants';
import { adminRoutes } from '@/constants/route-settings';

interface EditUserForm extends Omit<CustomUserDTO, 'directory_set'> {
  directory_set: string;
}

const cleanPhoneNumber = (phone: string) => phone.replace(/[^\d+]/g, '');

export function EditUserPage() {
  const navigate = useNavigate();
  const { updateUser, users } = useGlobalStore();
  const { getDirectories, directories } = useApiManagerStore();

  const {
    resetPassword,
    reset2FA,
    updateUserActiveStatus,
    resetToken,
    updateUserInfo,
  } = useAuthStore();

  const { uuid } = useParams<{ uuid: string }>();
  const [isLoading, setIsLoading] = useState(true);

  const [userToChangeStatus, setUserToChangeStatus] = useState<{
    uuid: string;
    is_active: boolean;
  } | null>(null);

  const [userToResetPassword, setUserToResetPassword] = useState<string | null>(
    null,
  );

  const [userToReset2FA, setUserToReset2FA] = useState<string | null>(null);

  const [userUuidToResetToken, setUserToResetToken] = useState<string | null>(
    null,
  );

  const [isCopied, setIsCopied] = useState(false);

  const form = useForm<EditUserForm>({
    defaultValues: {
      first_name: '',
      last_name: '',
      user_type: undefined,
      directory_set: '',
      insurance_area: undefined,
      phone_number: '',
      email: '',
      is_active: false,
      modified: '',
      created: '',
    },
  });

  const confirmChangeStatus = async () => {
    if (!userToChangeStatus) return;
    try {
      const statusChangeTo = userToChangeStatus.is_active;

      await updateUserActiveStatus(userToChangeStatus.uuid, statusChangeTo);
      const userData = users.find(
        (user) => user.uuid === userToChangeStatus.uuid,
      );

      if (userData) {
        await updateUser(userToChangeStatus.uuid, {
          ...userData,
          is_active: statusChangeTo,
        });
      }
      form.setValue('is_active', statusChangeTo);
      setUserToChangeStatus(null);
    } catch (error) {}
  };

  const confirmResetPassword = async () => {
    if (!userToResetPassword) return;
    await resetPassword(userToResetPassword);
    setUserToResetPassword(null);
  };

  const confirmReset2FA = async () => {
    if (!userToReset2FA) return;
    await reset2FA(userToReset2FA);
    setUserToReset2FA(null);
  };

  const confirmResetToken = async () => {
    if (!userUuidToResetToken) return;
    const { static_token } = await resetToken(userUuidToResetToken);
    const userData = users.find((user) => user.uuid === userUuidToResetToken);

    if (userData) {
      await updateUser(userUuidToResetToken, {
        ...userData,
        uuid: userUuidToResetToken,
        static_token,
      });
    }
    form.setValue('static_token', static_token);
    setUserToResetToken(null);
  };

  useEffect(() => {
    const fetchUser = async () => {
      // Then fetch user data
      if (!uuid) return;
      await getDirectories();

      let userData = users.find((user) => user.uuid === uuid);

      if (!userData) {
        userData = await apiService.getUser(uuid);
      }
      form.reset(
        {
          first_name: userData.first_name,
          last_name: userData.last_name,
          user_type: userData.user_type,
          directory_set:
            userData.user_type !== USER_ROLES.EXECUTOR.value &&
            userData.user_type !== USER_ROLES.EXTERNAL_SYSTEM.value
              ? 'Головний офіс'
              : userData.directory_set[0],
          insurance_area: userData.insurance_area,

          phone_number: userData.phone_number,
          email: userData.email.toLowerCase(),
          is_active: userData.is_active,
          modified: userData.modified,
          created: userData.created,
          static_token: userData.static_token,
        },
        {
          keepDefaultValues: false,
          keepTouched: false,
          keepDirty: false,
        },
      );
      setIsLoading(false);
    };

    fetchUser();
  }, [uuid]);

  const onSubmit = async (data: EditUserForm) => {
    try {
      if (!uuid) return;

      await updateUserInfo({
        user_type: data.user_type,
        phone_number: cleanPhoneNumber(data.phone_number),
        email: data.email,
        uuid: uuid,
      });

      await updateUser(uuid, {
        ...data,
        user_type: data.user_type,
        directory_set:
          data.user_type === USER_ROLES.EXECUTOR.value
            ? [data.directory_set]
            : directories.map((directory) => directory.uuid),
        is_active: true,
      });

      navigate(adminRoutes.users);
    } catch (error) {}
  };

  const watchStatus = form.watch('is_active');
  const isActive = watchStatus;

  const userType = form.watch('user_type');

  useEffect(() => {
    if (
      form.getFieldState('user_type').isDirty &&
      userType !== USER_ROLES.EXECUTOR.value
    ) {
      form.setValue('directory_set', 'Головний офіс', {
        shouldDirty: true,
        shouldTouch: true,
      });
    } else if (form.getFieldState('user_type').isDirty) {
      form.resetField('directory_set', {
        keepDirty: false,
        keepTouched: false,
        keepError: false,
      });
    }
  }, [userType, form]);

  const handleResetPassword = () => {
    setUserToResetPassword(form.getValues('email'));
  };

  const handleChangeStatus = () => {
    setUserToChangeStatus({
      uuid: uuid!,
      is_active: !isActive,
    });
  };

  const handleResetToken = () => {
    setUserToResetToken(uuid!);
  };

  const handleReset2FA = () => {
    setUserToReset2FA(uuid!);
  };

  const handleCopyToken = () => {
    navigator.clipboard.writeText(form.getValues('static_token') || '');
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <>
      <ConfirmationDialog
        isOpen={!!userToChangeStatus}
        onClose={() => setUserToChangeStatus(null)}
        onConfirm={confirmChangeStatus}
        variant={isActive ? 'destructive' : 'default'}
        title={`Ви впевнені, що бажаєте ${
          isActive ? 'заблокувати' : 'активувати'
        } цей запис?`}
        confirmText={isActive ? 'Заблокувати' : 'Активувати'}
      />
      <ConfirmationDialog
        isOpen={!!userToResetPassword}
        onClose={() => setUserToResetPassword(null)}
        onConfirm={confirmResetPassword}
        title="Ви впевнені, що бажаєте скинути пароль?"
        confirmText="Скинути"
      />
      <ConfirmationDialog
        isOpen={!!userToReset2FA}
        onClose={() => setUserToReset2FA(null)}
        onConfirm={confirmReset2FA}
        title="Ви впевнені, що бажаєте скинути 2FA?"
        confirmText="Скинути"
      />
      <ConfirmationDialog
        isOpen={!!userUuidToResetToken}
        onClose={() => setUserToResetToken(null)}
        onConfirm={confirmResetToken}
        title="Ви впевнені, що бажаєте оновити токен?"
        confirmText="Оновити"
      />

      <div className="flex w-full flex-col shadow-sm">
        <nav className="flex h-[50px] items-center border-b bg-white pl-5 sm:h-[70px] sm:pl-[50px]">
          <Link to={adminRoutes.users} className="flex items-center gap-2.5">
            <IconChevronLeft className="h-[14px] w-[14px] fill-blue-300" />
            <div className="flex gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded bg-blue-300">
                {form.getValues('user_type') ===
                USER_ROLES.EXTERNAL_SYSTEM.value ? (
                  <IconUserAdmin className="h-9 w-9" />
                ) : (
                  <IconUser className="h-9 w-9" />
                )}
              </div>
              <div className="flex flex-col">
                <div className="text-xs font-medium text-blue-300">
                  {form.getValues('first_name')} {form.getValues('last_name')}
                </div>
                <div className="flex items-baseline gap-1 leading-3 text-primary">
                  <span className="text-lg font-medium">
                    {form.getValues('email')}
                  </span>
                  <div
                    className={cn(
                      'h-[8px] w-[8px] rounded-full',
                      isActive ? 'bg-success' : 'bg-destructive',
                    )}
                  ></div>
                </div>
              </div>
            </div>
          </Link>
        </nav>
      </div>
      <div className="flex w-full flex-wrap justify-start gap-6 p-5 sm:p-12">
        <Card className="max-w-[920px] flex-1 rounded-md p-7 shadow-sm">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex grid-cols-2 flex-col sm:grid">
                {/* Basic Data Section Skeleton */}
                <div className="col-span-1 row-span-2 space-y-6 border-b border-r-0 pb-7 sm:border-b-0 sm:border-r sm:pb-0 sm:pr-7">
                  <div className="space-y-2">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-6 w-72" />
                  </div>
                  <div className="flex flex-col gap-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} className="h-[50px] w-full" />
                    ))}
                  </div>
                </div>

                {/* Contact Data Section Skeleton */}
                <div className="space-y-6 pl-7">
                  <div className="space-y-2">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-6 w-72" />
                  </div>
                  <div className="flex flex-col gap-4">
                    {[1, 2].map((i) => (
                      <Skeleton key={i} className="h-[50px] w-full" />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  <div className="flex grid-cols-2 flex-col sm:grid">
                    {/* Basic Data Section */}
                    <div className="col-span-1 row-span-2 space-y-6 border-b border-r-0 pb-7 sm:border-b-0 sm:border-r sm:pb-0 sm:pr-7">
                      <div>
                        <h2 className="mb-2 text-2xl font-semibold">
                          Основні дані
                        </h2>
                        <p className="mb-6 text-primary">
                          Блок основних даних користувача системи
                        </p>
                      </div>

                      <div className="flex flex-col gap-4">
                        <FormField
                          control={form.control}
                          name="last_name"
                          rules={{ required: "Обов'язкове поле" }}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input placeholder="Прізвище" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="first_name"
                          rules={{ required: "Обов'язкове поле" }}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input placeholder="Ім'я" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="user_type"
                          rules={{ required: "Обов'язкове поле" }}
                          render={({ field }) => (
                            <FormItem>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                disabled={
                                  !field.value ||
                                  field.value === USER_ROLES.ADMIN.value ||
                                  field.value ===
                                    USER_ROLES.EXTERNAL_SYSTEM.value
                                }
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Тип користувача" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value={USER_ROLES.EXECUTOR.value}>
                                    <span>{USER_ROLES.EXECUTOR.title}</span>
                                  </SelectItem>
                                  <SelectItem
                                    value={USER_ROLES.CONTROLLER.value}
                                  >
                                    <span>{USER_ROLES.CONTROLLER.title}</span>
                                  </SelectItem>
                                  <SelectItem
                                    value={USER_ROLES.EXTERNAL_SYSTEM.value}
                                    disabled
                                  >
                                    <span>
                                      {USER_ROLES.EXTERNAL_SYSTEM.title}
                                    </span>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="directory_set"
                          rules={{ required: "Обов'язкове поле" }}
                          render={({ field }) => (
                            <FormItem>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                disabled={
                                  userType !== USER_ROLES.EXECUTOR.value
                                }
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Дирекція" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {userType !== USER_ROLES.EXECUTOR.value && (
                                    <SelectItem value="Головний офіс">
                                      Головний офіс
                                    </SelectItem>
                                  )}
                                  {directories.map((dept) => (
                                    <SelectItem
                                      key={dept.uuid}
                                      value={dept.uuid}
                                    >
                                      {dept.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="insurance_area"
                          rules={{ required: "Обов'язкове поле" }}
                          render={({ field }) => (
                            <FormItem>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Напрямок" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {Object.values(INSURANCE_AREA).map(
                                    (direction) => (
                                      <SelectItem
                                        key={direction}
                                        value={direction}
                                      >
                                        {direction}
                                      </SelectItem>
                                    ),
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    {/* Contact Data Section */}
                    <div className="col-start-2 row-start-1 mb-7 mt-5 space-y-6 sm:mb-0 sm:mt-0 sm:pl-7">
                      <div>
                        <h2 className="mb-2 text-2xl font-semibold">
                          Контактні дані
                        </h2>
                        <p className="mb-6 text-primary">
                          Блок контактних даних користувача системи
                        </p>
                      </div>

                      <div className="flex flex-col gap-4">
                        <FormField
                          control={form.control}
                          name="phone_number"
                          rules={{
                            required: "Обов'язкове поле",
                            pattern: {
                              value: /^\+38\s?\(0\d{2}\)\s?\d{3}-\d{2}-\d{2}$/,
                              message:
                                'Невірний формат телефону, наприклад +38 (099) 999-99-99',
                            },
                          }}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <InputMask
                                  mask="+38 (099) 999-99-99"
                                  maskChar="_"
                                  value={field.value || ''}
                                  onChange={field.onChange}
                                  onBlur={field.onBlur}
                                >
                                  {(inputProps) => {
                                    return (
                                      <input
                                        {...inputProps}
                                        placeholder="Телефон"
                                        type="tel"
                                        autoComplete="tel"
                                        className={cn(
                                          'peer relative z-10 flex h-[50px] w-full rounded-md border border-gray-200 bg-transparent',
                                          'px-5 py-3.5 text-base file:border-0 file:bg-transparent file:text-sm file:font-medium',
                                          'focus-visible:outline-none',
                                          'hover:border-primary hover:shadow-[0px_0px_5px_2px_#00000040] md:px-3.5 md:py-2 md:text-sm lg:text-base',
                                          'transition-color disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:shadow-none',
                                          field.value
                                            ? 'text-primary'
                                            : 'text-gray-200',
                                        )}
                                        aria-describedby="phone-number-description"
                                      />
                                    );
                                  }}
                                </InputMask>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="email"
                          rules={{
                            required: "Обов'язкове поле",
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Невірний формат email',
                            },
                          }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col space-y-1">
                              <FormControl>
                                <Input
                                  {...field}
                                  type="email"
                                  placeholder="Email"
                                />
                              </FormControl>
                              <FormMessage />
                              <span className="text-center text-xs text-gray-200">
                                Заповніть{' '}
                                <span className="text-xs text-destructive">
                                  усі поля
                                </span>{' '}
                                для можливості додання користувача
                              </span>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end sm:col-start-2 sm:row-start-2 sm:gap-4 sm:self-end">
                      <Button
                        type="submit"
                        variant="destructive"
                        disabled={
                          !Object.keys(form.formState.dirtyFields).length
                        }
                      >
                        Зберегти
                      </Button>
                    </div>
                  </div>
                </form>
              </Form>
            )}
          </CardContent>
        </Card>

        <Card className="h-auto max-w-[460px] flex-1 rounded-md p-7 shadow-sm">
          <CardContent className="space-y-6 p-0">
            {isLoading ? (
              <>
                {/* Token Section Skeleton */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-4 w-72" />
                  </div>
                  <Skeleton className="h-[50px] w-full" />
                  <div className="flex justify-between gap-4">
                    <Skeleton className="h-10 w-[150px]" />
                    <Skeleton className="h-10 w-[150px]" />
                  </div>
                </div>

                {/* Access Management Skeleton */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-4 w-72" />
                  </div>
                  <div className="flex justify-between gap-4">
                    <Skeleton className="h-10 w-[150px]" />
                    <Skeleton className="h-10 w-[150px]" />
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Token Access and 2FA Section */}
                <div>
                  <h2 className="mb-2 text-2xl font-semibold">
                    Токен доступу та 2FA
                  </h2>
                  <p className="mb-6 text-primary">
                    Це токен доступу до системи "Фотоархів"
                  </p>

                  <div className="mb-5 flex">
                    <input
                      value={form.getValues('static_token') || ''}
                      readOnly
                      className="h-[50px] w-full rounded-l-[10px] border border-r-0 border-dashed border-gray-200 pl-5 pr-4"
                    />

                    <Button
                      variant="destructive"
                      size="icon"
                      className="relative h-auto min-w-[50px] rounded-l-none"
                      type="button"
                      onClick={handleCopyToken}
                    >
                      <IconCopy className="h-4 w-4" />
                      {isCopied && (
                        <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-primary px-2 py-1 text-xs text-white">
                          Скопійовано!
                        </span>
                      )}
                    </Button>
                  </div>

                  <div className="mb-6 flex justify-between gap-4">
                    <Button
                      variant="outline"
                      size="default"
                      className="w-[150px] p-0"
                      onClick={handleResetToken}
                      type="button"
                    >
                      Оновити токен
                    </Button>

                    <Button
                      variant="outline"
                      size="default"
                      className="w-[150px] p-0"
                      onClick={handleReset2FA}
                      type="button"
                    >
                      Скинути 2FA
                    </Button>
                  </div>
                </div>

                <div>
                  <h2 className="mb-2 text-2xl font-semibold">
                    Управління доступом
                  </h2>
                  <p className="mb-6 text-primary">
                    Даний функціонал дозволяє керувати доступом до системи для
                    конкретного користувача
                  </p>

                  <div className="flex justify-between gap-4">
                    <Button
                      variant="outline"
                      size="default"
                      className="w-[150px] p-0"
                      onClick={handleResetPassword}
                      disabled={!uuid}
                      type="button"
                    >
                      Скинути пароль
                    </Button>
                    <Button
                      variant={isActive ? 'outlineDestructive' : 'outline'}
                      size="default"
                      className="w-[150px] p-0"
                      onClick={handleChangeStatus}
                      disabled={!uuid}
                      type="button"
                    >
                      {isActive ? 'Заблокувати' : 'Активувати'}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
